// The Prices page's places as the rail and the palette list them, and the one rule for moving to each.

import { Calendar1, ChartCandlestick, CloudUpload, type LucideIcon } from "lucide-react";
import { en, type Translations } from "../../i18n/en";
import type { CalendarViewKey } from "../../navigation/calendarViews";
import { routePath, type AppRoute } from "../../navigation/routes";
import type { DraftsViewMode } from "../Drafts";

type DraftsRoute = Extract<AppRoute, { page: "drafts" }>;

export const PRICES_PLACE_TABS = ["calendar", "priceHistory", "uploadLog"] as const satisfies readonly DraftsViewMode[];

export type PricesPlaceTab = (typeof PRICES_PLACE_TABS)[number];

/** Glyphs no rail destination, pricing section or account section wears (`prices-places.test.ts`). */
export const PRICES_TAB_ICONS: Readonly<Record<PricesPlaceTab, LucideIcon>> = {
    calendar: Calendar1,
    priceHistory: ChartCandlestick,
    uploadLog: CloudUpload,
};

export function pricesTabRoute(tab: PricesPlaceTab): DraftsRoute {
    return { page: "drafts", tab };
}

/** A view's address names no stay, so it is the same link from every page. */
export function calendarViewRoute(view: CalendarViewKey): DraftsRoute {
    return { page: "drafts", calendarView: view };
}

export function calendarViewHref(view: CalendarViewKey): string {
    return routePath(calendarViewRoute(view));
}

/** The tab on screen, or null off Prices. A framed stay holds the calendar whatever tab the address names. */
export function currentPricesTab(route: AppRoute | null | undefined): PricesPlaceTab | null {
    if (route?.page !== "drafts") return null;
    if (route.stay) return "calendar";
    return route.tab ?? "calendar";
}

/** Whether the rail lists the tabs and views under Prices for this reader. */
export function pricesBranchDrawn(reader: { frontDesk: boolean; locked: boolean; onboarding: boolean }): boolean {
    return !reader.frontDesk && !reader.locked && !reader.onboarding;
}

export interface PricesPlaceLabels {
    prices: string;
    calendar: string;
    priceHistory: string;
    uploadLog: string;
    /** The views' heading, and the name of the button that opens them. */
    views: string;
    /** Read with the folded Prices icon by a screen reader alone. */
    flyoutHint: string;
    /** Stands in for the views where they cannot be drawn. */
    unavailableHint: string;
}

export interface PricesPlaceTranslations {
    sidebar?: Partial<Translations["sidebar"]>;
    drafts?: Partial<Translations["drafts"]>;
    priceHistory?: Partial<Translations["priceHistory"]>;
    uploadLog?: Partial<Translations["uploadLog"]>;
}

/** The page's own names for its places, defaulted name by name from English: the harnesses hand partial tables. */
export function pricesPlaceLabels(t: PricesPlaceTranslations): PricesPlaceLabels {
    return {
        prices: t.sidebar?.drafts ?? en.sidebar.drafts,
        calendar: t.drafts?.viewCalendar ?? en.drafts.viewCalendar,
        priceHistory: t.priceHistory?.tabTitle ?? en.priceHistory.tabTitle,
        uploadLog: t.uploadLog?.tabTitle ?? en.uploadLog.tabTitle,
        views: t.drafts?.viewLabel ?? en.drafts.viewLabel,
        flyoutHint: t.sidebar?.pricesFlyoutHint ?? en.sidebar.pricesFlyoutHint,
        unavailableHint: t.sidebar?.unavailableHint ?? en.sidebar.unavailableHint,
    };
}

/**
 * What a move did. `pushed`: handed to the shell, which pushes where the page is allowed. `inPlace`: the calendar's
 * entry rewritten. `none`: already on screen. Nothing changes the address in `none`, so a caller closes its own layers.
 */
export type PricesMove = "pushed" | "inPlace" | "none";

interface PricesMoveContext {
    /** The route on screen. */
    route: AppRoute;
    /** The shell's move, which checks the page is open to the reader. */
    navigateTo: (route: AppRoute) => void;
}

export function openPricesTab(tab: PricesPlaceTab, { route, navigateTo }: PricesMoveContext): PricesMove {
    if (currentPricesTab(route) === tab) return "none";
    navigateTo(pricesTabRoute(tab));
    return "pushed";
}

interface CalendarViewMoveContext extends PricesMoveContext {
    /** `navigation/history`'s `navigate`, for the rewrite in place. */
    navigate: (to: string, options: { replace: boolean }) => void;
    /** The view the calendar draws (`useShownCalendarView`), or null. */
    shownView: CalendarViewKey | null;
}

/**
 * Off the calendar a view is a push of its one-time request. On it the entry is rewritten, so Back never lands on an
 * identical page; the page applies the request and replaces it back, the stay kept.
 */
export function openCalendarView(
    view: CalendarViewKey,
    { route, navigateTo, navigate, shownView }: CalendarViewMoveContext,
): PricesMove {
    if (route.page !== "drafts" || currentPricesTab(route) !== "calendar") {
        navigateTo(calendarViewRoute(view));
        return "pushed";
    }
    if (shownView === view) return "none";
    navigate(routePath({ page: "drafts", tab: "calendar", stay: route.stay, calendarView: view }), { replace: true });
    return "inPlace";
}

/**
 * What the three forms of the Prices entry take (`PricesTree` open, `PricesFlyout` folded, `PricesPhonePanel` in the
 * phone's menu). `PricesDestination` reads the route, the access and the shown view and hands this down; the forms
 * move nothing themselves, so the rules of `openPricesTab` / `openCalendarView` live in one place.
 */
export interface PricesFormProps {
    /** `currentPricesTab(route)`: the tab on screen, or null off Prices. */
    currentTab: PricesPlaceTab | null;
    /** The view the calendar draws while it is on screen, else null; the rail's mark and the Kalendář row's trailing text. */
    shownView: CalendarViewKey | null;
    labels: PricesPlaceLabels;
    /** For the views list: the property whose tracking decides the market views, and its boundary's retry key. */
    connectionId: number | null | undefined;
    connectionVersion: number;
    /** Whether the views list may read the tracking switch from the server (never for the front desk, a locked or an elsewhere-opened property). */
    readTracking: boolean;
    /** A tab picked; the form closes its own layer after it. */
    onOpenTab: (tab: PricesPlaceTab) => void;
    /** A view picked; the form closes its own layer after it. */
    onOpenView: (view: CalendarViewKey) => void;
}
